/**
 * UserMenu — the sidebar footer user affordance.
 *
 * Single source for the "account area" overlay shown when a user clicks
 * their avatar/role strip at the bottom of the sidebar. Owns the logout
 * action so there is exactly one place in the UI where "sign out" lives.
 *
 * Keeping it in its own component follows SRP: the sidebar handles nav,
 * the menu handles identity actions. Re-usable from the command palette
 * once that lands (Phase 12).
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Settings, Keyboard, HelpCircle, LogOut, SunMoon, Check, User as UserIcon, CreditCard } from 'lucide-react';
import type { BackendUserWithPlan } from '../../../userService';
import { logout } from '../../../authService';
import { useTheme, type ThemeChoice } from '../../../core/theme/ThemeProvider';
import './UserMenu.css';

type Props = {
    /** Anchor element the menu is positioned relative to. */
    anchor: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    user: BackendUserWithPlan | null;
    onAccountSettings: () => void;
    /** Optional: routes to /profile. When absent the entry is hidden. */
    onProfile?: () => void;
    /** Optional: routes to /settings#billing. When absent the entry is hidden. */
    onBilling?: () => void;
    onKeyboardShortcuts?: () => void;
    onHelp?: () => void;
};

const THEME_LABELS: Record<ThemeChoice, string> = {
    system: 'Match system',
    light: 'Light',
    dark: 'Dark',
};
const THEME_ORDER: ThemeChoice[] = ['system', 'light', 'dark'];

const ROLE_LABELS: Record<string, string> = {
    admin: 'Admin',
    mentor: 'Mentor',
    mentee: 'Mentee',
};

function UserMenu({
    anchor,
    open,
    onClose,
    user,
    onAccountSettings,
    onProfile,
    onBilling,
    onKeyboardShortcuts,
    onHelp,
}: Props) {
    const menuRef = useRef<HTMLDivElement>(null);
    const [loggingOut, setLoggingOut] = useState(false);
    const [themeSubmenuOpen, setThemeSubmenuOpen] = useState(false);
    const { choice: themeChoice, setChoice: setThemeChoice } = useTheme();

    // Close on outside click or Escape. Scoped to mount so no stale listeners.
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (menuRef.current?.contains(target)) return;
            if (anchor?.contains(target)) return;
            onClose();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose, anchor]);

    if (!open || !anchor) return null;

    const rect = anchor.getBoundingClientRect();
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
    // CSS-variable-driven positioning so no inline `style` is needed on
    // `<div class="user-menu">`. Mobile takes a bottom-sheet layout
    // (full-width, anchored to the safe-area); desktop anchors to the
    // right of the sidebar footer trigger and clamps so it never
    // overflows a narrow window.
    const MENU_WIDTH = 260;
    const MENU_GAP = 8;
    const positionStyle = (isMobile
        ? {
            '--user-menu-left': '12px',
            '--user-menu-right': '12px',
            '--user-menu-bottom': '16px',
        }
        : {
            '--user-menu-left': `${Math.min(rect.right + MENU_GAP, window.innerWidth - MENU_WIDTH - 16)}px`,
            '--user-menu-bottom': `${Math.max(window.innerHeight - rect.bottom, 12)}px`,
        }
    ) as unknown as React.CSSProperties;

    const displayName = user
        ? (user.first_name || user.last_name
            ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
            : user.username)
        : '—';
    const roleKey = user?.profile?.role;
    const roleLabel = roleKey && ROLE_LABELS[roleKey] ? ROLE_LABELS[roleKey] : null;
    const planName = user?.active_plan?.plan?.name ?? null;

    const handleLogout = async () => {
        if (loggingOut) return;
        setLoggingOut(true);
        try {
            await logout();
        } catch {
            // logout() handles its own errors; never leave the user stuck here.
            setLoggingOut(false);
        }
    };

    return createPortal(
        // `role="dialog"` rather than `menu` so we can host richer content
        // (user header, theme radiogroup) without violating the WAI-ARIA
        // menu-role's strict child rules.
        <div
            ref={menuRef}
            className="user-menu"
            role="dialog"
            aria-label="Account menu"
            style={positionStyle}
        >
            <header className="user-menu__header">
                <p className="user-menu__name">{displayName}</p>
                {user?.email && <p className="user-menu__email">{user.email}</p>}
                {(roleLabel || planName) && (
                    <p className="user-menu__meta">
                        {roleLabel}{roleLabel && planName ? ' · ' : ''}{planName}
                    </p>
                )}
            </header>

            <div className="user-menu__divider" role="separator" />

            {onProfile && (
                <button
                    type="button"
                    className="user-menu__item"
                    onClick={() => { onProfile(); onClose(); }}
                >
                    <UserIcon size={16} />
                    <span>Your profile</span>
                </button>
            )}

            <button
                type="button"
                className="user-menu__item"
                onClick={() => { onAccountSettings(); onClose(); }}
            >
                <Settings size={16} />
                <span>Account settings</span>
            </button>

            {onBilling && (
                <button
                    type="button"
                    className="user-menu__item"
                    onClick={() => { onBilling(); onClose(); }}
                >
                    <CreditCard size={16} />
                    <span>Billing</span>
                </button>
            )}

            <div
                className={`user-menu__theme ${themeSubmenuOpen ? 'is-open' : ''}`}
            >
                <button
                    type="button"
                    className="user-menu__item"
                    aria-expanded={themeSubmenuOpen ? 'true' : 'false'}
                    onClick={() => setThemeSubmenuOpen((v) => !v)}
                >
                    <SunMoon size={16} />
                    <span className="user-menu__item-label">Theme</span>
                    <span className="user-menu__item-value">
                        {THEME_LABELS[themeChoice]}
                    </span>
                </button>
                {themeSubmenuOpen && (
                    <div className="user-menu__theme-options">
                        {THEME_ORDER.map((opt) => {
                            const selected = opt === themeChoice;
                            return (
                                <button
                                    key={opt}
                                    type="button"
                                    aria-pressed={selected ? 'true' : 'false'}
                                    className={`user-menu__theme-option ${selected ? 'is-active' : ''}`}
                                    onClick={() => {
                                        setThemeChoice(opt);
                                        setThemeSubmenuOpen(false);
                                    }}
                                >
                                    <span>{THEME_LABELS[opt]}</span>
                                    {selected && <Check size={14} aria-hidden="true" />}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {onKeyboardShortcuts && (
                <button
                    type="button"
                    className="user-menu__item"
                    onClick={() => { onKeyboardShortcuts(); onClose(); }}
                >
                    <Keyboard size={16} />
                    <span>Keyboard shortcuts</span>
                </button>
            )}

            {onHelp && (
                <button
                    type="button"
                    className="user-menu__item"
                    onClick={() => { onHelp(); onClose(); }}
                >
                    <HelpCircle size={16} />
                    <span>Help &amp; support</span>
                </button>
            )}

            <div className="user-menu__divider" role="separator" />

            <button
                type="button"
                className="user-menu__item user-menu__item--danger"
                onClick={handleLogout}
                disabled={loggingOut}
            >
                <LogOut size={16} />
                <span>{loggingOut ? 'Logging out…' : 'Log out'}</span>
            </button>
        </div>,
        document.body,
    );
}

export default UserMenu;
